import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Business,
  Check,
  CheckCircle,
  Close,
  Delete,
  OpenInNew,
  PersonRemove,
  ContentCopy,
  CallSplit,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import { AppHeader } from '../components/app-header';
import { ProviderPassport } from '../components/provider-passport';
import { WorkspaceSidebar } from '../components/org-page-redesign/WorkspaceSidebar';
import { buildProviderNavGroups } from '../components/provider-nav';
import { ConvertToOrgDialog } from '../components/ConvertToOrgDialog';
import { PrescribingProxyToggle } from '../components/prescribing-proxy-toggle';
import { CopyTemplateDialog } from '../components/CopyTemplateDialog';
import { DataTable } from '../components/data-table';
import { EditProviderPhoneDialog } from '../components/EditProviderPhoneDialog';
import { ChangeProviderEmailDialog } from '../components/ChangeProviderEmailDialog';
import { FilterableDataTable } from '../components/filterable-data-table';
import { LoadingState } from '../components/loading-state';
import { ProviderBundleAssignmentsSection } from '../components/provider-bundle-assignments-section';
import { ProviderEncounterTypesSection } from '../components/provider-encounter-types-section';
import { ProviderFeatureFlags } from '../components/provider-feature-flags';
import { ProviderStatsigGates } from '../components/provider-statsig-gates';
import { ProviderEntitlementsSection } from '../components/provider-entitlements-section';
import { VoiceEnrollmentSection } from '../components/voice-enrollment-section';
import {
  useGetProviderInfoQuery,
  useGetProviderNotesQuery,
  useGetProviderNoteProcessingSessionsQuery,
  useGetProviderGoogleSyncStatusQuery,
  useInitiateProviderGoogleSyncMutation,
  useDeleteProviderGoogleCalendarConfigMutation,
  useGetRecentStreamingThreadsQuery,
  useGetV2AdminOrganizationsByOrganizationIdQuery,
  useUpdateDosespotOnboardingStatusMutation,
} from '../store/api';
import type { DosespotOnboardingStatus, StreamingThreadListItem } from '../store/api';
import {
  useDeleteAdminNoteTemplateMutation,
  useForkTemplateForUserMutation,
  useGetAdminProviderTemplatesQuery,
  useRemoveUserTemplateMutation,
  useUpdateProviderProfileMutation,
  type EffectiveNoteTemplateSummary,
} from '../store/hooks';
import { formatDateTime, formatRelativeTime, getStatusBadgeColor } from '../utils/formatting';

export default function ProviderDetailPage() {
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const copy = useCopyToClipboard();

  // Handle URL encoding issues
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // Fork dialog state
  const [forkDialogOpen, setForkDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [bypassForkRestriction, setBypassForkRestriction] = useState(false);

  // Copy / convert dialog state for the templates list
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [templateForAction, setTemplateForAction] = useState<EffectiveNoteTemplateSummary | null>(
    null,
  );

  // Templates table state (scope filter + pagination)
  const [templatesScope, setTemplatesScope] = useState<'all' | 'public' | 'org' | 'user'>('all');
  const [templatesPage, setTemplatesPage] = useState(1);
  const [templatesPerPage, setTemplatesPerPage] = useState(20);

  // Nav rail state
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [sectionParams, setSectionParams] = useSearchParams();

  // Phone edit dialog state
  const [editPhoneDialogOpen, setEditPhoneDialogOpen] = useState(false);
  const [changeEmailDialogOpen, setChangeEmailDialogOpen] = useState(false);
  const [deletePhoneDialogOpen, setDeletePhoneDialogOpen] = useState(false);

  // Phone delete mutation
  const [deletePhone, { isLoading: isDeletingPhone }] = useUpdateProviderProfileMutation();

  // Fork mutation
  const [forkTemplate, { isLoading: isForking }] = useForkTemplateForUserMutation();

  // Template removal/deletion mutations
  const [removeUserTemplate, { isLoading: isRemoving }] = useRemoveUserTemplateMutation();
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteAdminNoteTemplateMutation();
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [templateToRemove, setTemplateToRemove] = useState<any>(null);
  const [removeAction, setRemoveAction] = useState<'remove' | 'delete'>('remove');

  // Call all hooks unconditionally (React rules of hooks)
  const {
    data: provider,
    isLoading: userLoading,
    error: userError,
  } = useGetProviderInfoQuery({ providerId: providerId || '' }, { skip: !providerId });

  // Fetch the provider's org so the JotRX section can render connection state
  // (the org owns the DoseSpot clinic; without it nothing the provider does here matters).
  const { data: providerOrg, isLoading: providerOrgLoading } =
    useGetV2AdminOrganizationsByOrganizationIdQuery(
      { organizationId: provider?.organization_id || '' },
      { skip: !provider?.organization_id },
    );

  const {
    data: notesData,
    isLoading: _notesLoading,
    error: _notesError,
  } = useGetProviderNotesQuery(
    {
      providerId: providerId || '',
      perPage: 5,
    },
    { skip: !providerId },
  );

  const {
    data: noteProcessingSessionsData,
    isLoading: _noteProcessingSessionsLoading,
    error: _noteProcessingSessionsError,
  } = useGetProviderNoteProcessingSessionsQuery(
    {
      providerId: providerId || '',
      perPage: 5,
    },
    { skip: !providerId },
  );

  // Streaming threads data
  const {
    data: streamingThreadsData,
    isLoading: _streamingThreadsLoading,
    error: _streamingThreadsError,
  } = useGetRecentStreamingThreadsQuery(
    {
      userId: providerId || '',
      limit: 5,
      workflowName: 'note_chat',
    },
    { skip: !providerId },
  );

  // Google Calendar admin data
  const { data: googleSync, isLoading: googleSyncLoading } = useGetProviderGoogleSyncStatusQuery(
    { providerId: providerId || '' },
    { skip: !providerId },
  );
  const [initiateSync, { isLoading: initiating }] = useInitiateProviderGoogleSyncMutation();
  const [deleteCalendar, { isLoading: deleting }] = useDeleteProviderGoogleCalendarConfigMutation();

  // DoseSpot onboarding status
  const currentDosespotStatus = provider?.dosespot_onboarding_status as
    | DosespotOnboardingStatus
    | null
    | undefined;
  const [dosespotStatus, setDosespotStatus] = useState<DosespotOnboardingStatus | ''>('');
  const [updateDosespotOnboardingStatus, { isLoading: isUpdatingDosespot }] =
    useUpdateDosespotOnboardingStatusMutation();

  useEffect(() => {
    if (currentDosespotStatus) {
      setDosespotStatus(currentDosespotStatus);
    }
  }, [currentDosespotStatus]);

  // Templates data - fetch templates that provider actually sees
  const {
    data: templatesData,
    isLoading: _templatesLoading,
    error: _templatesError,
  } = useGetAdminProviderTemplatesQuery(
    {
      providerId: providerId || '',
      organizationId: provider?.organization_id || '',
    },
    { skip: !providerId || !provider?.organization_id },
  );

  // Use response - templates are pre-sorted by display_order
  // Apply scope filter, then paginate client-side (API doesn't paginate yet).
  const allTemplates: EffectiveNoteTemplateSummary[] = templatesData?.templates ?? [];
  const filteredTemplates = allTemplates.filter(template => {
    if (templatesScope === 'all') return true;
    return template.template?.owner_scope === templatesScope;
  });
  const totalTemplates = filteredTemplates.length;
  const paginatedTemplates = filteredTemplates.slice(
    (templatesPage - 1) * templatesPerPage,
    templatesPage * templatesPerPage,
  );

  const getTemplatesRowUrl = useCallback(
    (template: EffectiveNoteTemplateSummary) => {
      const templateId = template.template?.id || '';
      return `/admin-v2/provider/${encodeURIComponent(providerId || '')}/templates/${templateId}`;
    },
    [providerId],
  );

  // All useCallback hooks must be called before any early returns (React Rules of Hooks)
  // Get URL for each row - Note Processing Sessions
  const getNoteProcessingSessionsRowUrl = useCallback((nps: any) => {
    return `/admin-v2/note-processing-session/${nps.note_processing_session_id}`;
  }, []);

  // Get state for each row - Note Processing Sessions
  const getNoteProcessingSessionsRowState = useCallback(() => {
    return {
      user: {
        userId: provider?.provider_id || '',
        name: provider?.name || '',
        email: provider?.email || '',
      },
      providerId,
    };
  }, [provider, providerId]);

  // Get URL for Note Processing Sessions view all
  const getNoteProcessingSessionsViewAllUrl = useCallback(() => {
    return `/admin-v2/provider/${encodeURIComponent(providerId || '')}/note-processing-sessions`;
  }, [providerId]);

  const getStreamingThreadsRowUrl = useCallback(
    (thread: StreamingThreadListItem) => {
      const base = `/admin-v2/streaming-thread/${thread.thread_id}`;
      return providerId ? `${base}?userId=${encodeURIComponent(providerId)}` : base;
    },
    [providerId],
  );

  // Get URL for Streaming Threads view all
  const getStreamingThreadsViewAllUrl = useCallback(() => {
    return `/admin-v2/provider/${encodeURIComponent(providerId || '')}/streaming-threads`;
  }, [providerId]);

  // Copy / convert handlers for the inlined templates list
  const handleCopyClick = useCallback(
    (template: EffectiveNoteTemplateSummary, e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      setTemplateForAction(template);
      setCopyDialogOpen(true);
    },
    [],
  );

  const handleConvertClick = useCallback(
    (template: EffectiveNoteTemplateSummary, e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      setTemplateForAction(template);
      setConvertDialogOpen(true);
    },
    [],
  );

  // Fork template handler
  const handleOpenForkDialog = useCallback((template: any, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedTemplate(template);
    setBypassForkRestriction(false);
    setForkDialogOpen(true);
  }, []);

  const handleCloseForkDialog = useCallback(() => {
    setForkDialogOpen(false);
    setSelectedTemplate(null);
    setBypassForkRestriction(false);
  }, []);

  const handleForkTemplate = useCallback(async () => {
    if (!selectedTemplate || !providerId || !provider) return;

    try {
      const result = await forkTemplate({
        adminForkTemplateForUserRequest: {
          source_template_id: selectedTemplate.template?.id || '',
          target_user_id: providerId,
          target_organization_id: provider.organization_id || '',
          bypass_fork_restriction: bypassForkRestriction || undefined,
        },
      }).unwrap();

      handleCloseForkDialog();

      if (result.already_existed) {
        enqueueSnackbar('User already has a customized version of this template', {
          variant: 'info',
        });
      } else {
        enqueueSnackbar('Template forked successfully', { variant: 'success' });
      }

      // Navigate to the forked template editor
      if (result.template?.id) {
        navigate(`/admin-v2/templates/${result.template.id}`);
      }
    } catch (error: any) {
      if (error?.status === 403) {
        enqueueSnackbar(
          "This template doesn't allow user forks. Enable 'Force fork' to override.",
          { variant: 'error' },
        );
      } else {
        enqueueSnackbar(error?.data?.message || 'Failed to fork template', { variant: 'error' });
      }
    }
  }, [
    selectedTemplate,
    providerId,
    provider,
    bypassForkRestriction,
    forkTemplate,
    navigate,
    enqueueSnackbar,
    handleCloseForkDialog,
  ]);

  // Remove/delete template handlers
  const handleOpenRemoveDialog = useCallback(
    (template: any, action: 'remove' | 'delete', e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      setTemplateToRemove(template);
      setRemoveAction(action);
      setRemoveDialogOpen(true);
    },
    [],
  );

  const handleCloseRemoveDialog = useCallback(() => {
    setRemoveDialogOpen(false);
    setTemplateToRemove(null);
  }, []);

  const handleConfirmTemplateAction = useCallback(async () => {
    if (!templateToRemove || !providerId) return;
    const templateId = templateToRemove.template?.id || '';
    try {
      if (removeAction === 'delete') {
        await deleteTemplate({ templateId }).unwrap();
        enqueueSnackbar('Template permanently deleted', { variant: 'success' });
      } else {
        await removeUserTemplate({ userId: providerId, templateId }).unwrap();
        enqueueSnackbar('Template removed from user', { variant: 'success' });
      }
      handleCloseRemoveDialog();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || error?.data?.detail || 'Failed to remove template', {
        variant: 'error',
      });
    }
  }, [
    templateToRemove,
    providerId,
    removeAction,
    deleteTemplate,
    removeUserTemplate,
    enqueueSnackbar,
    handleCloseRemoveDialog,
  ]);

  // State getter for "View All" actions
  const getViewAllState = useCallback(() => {
    return {
      user: {
        userId: provider?.provider_id || '',
        name: provider?.name || '',
        email: provider?.email || '',
      },
      providerId,
    };
  }, [provider, providerId]);

  // Tab change handler
  const handleSectionSelect = useCallback(
    (key: string) => {
      // Tools entries are links to other pages, not sections of this one.
      if (key === 'shadow-notes') {
        const params = new URLSearchParams({ organizationId: provider?.organization_id || '' });
        navigate(`/admin-v2/shadow-notes?${params.toString()}`);
        return;
      }
      if (key === 'patient-containers') {
        navigate(
          `/admin-v2/provider/${encodeURIComponent(providerId || '')}/containers/patient_page`,
        );
        return;
      }
      if (['encounters', 'appointments', 'transcriptions', 'notes'].includes(key)) {
        navigate(`/admin-v2/provider/${encodeURIComponent(providerId || '')}/${key}`);
        return;
      }
      if (key === 'session-replays') {
        const now = Date.now();
        const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
        const filters = {
          filterStartTime: sevenDaysAgo,
          filterEndTime: now,
          filterEventsLogged: [
            {
              name: 'statsig::session_recording',
              filters: [
                {
                  property: { key: 'userID', column: 'user_object' },
                  operator: 'ANY_OF',
                  values: [provider?.provider_id],
                },
              ],
            },
          ],
        };
        const url = `https://console.statsig.com/4x1Dwzy0iHPy51koMolh2U/session_replay?filters=${encodeURIComponent(JSON.stringify(filters))}`;
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      setActiveSection(key);
    },
    [navigate, provider?.organization_id, provider?.provider_id, providerId],
  );

  // Deep link into a section (?section=encounters) — used by provider
  // subpage layouts whose nav rail points back at this page's sections.
  useEffect(() => {
    const section = sectionParams.get('section');
    if (section && provider) {
      handleSectionSelect(section);
      const next = new URLSearchParams(sectionParams);
      next.delete('section');
      setSectionParams(next, { replace: true });
    }
  }, [sectionParams, setSectionParams, provider, handleSectionSelect]);

  // If we don't have a providerId, we can't proceed
  if (!providerId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <p className="text-gray-600 mt-2">
              No provider ID specified. Please search for the provider to view their details.
            </p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Loading state
  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading provider details..." />
        </main>
      </div>
    );
  }

  // Error state
  if (userError || !provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading provider</h2>
            <p className="text-gray-600 mt-2">
              {userError && 'data' in userError
                ? JSON.stringify(userError.data)
                : 'Failed to load provider details. Please try again.'}
            </p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const noteProcessingSessionsColumns = [
    {
      key: 'note_processing_session_id',
      label: 'Session ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 break-all font-mono">
            {value || 'N/A'}
          </span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  copy(value, 'Session ID');
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'encounter_id',
      label: 'Encounter',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-900 break-all font-mono">{value || 'N/A'}</span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  copy(value, 'Encounter ID');
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'transcription_session_ids',
      label: 'Transcripts',
      render: (value: string[] | null) => (
        <span className="text-sm text-gray-900">{value?.length || 0}</span>
      ),
    },
    {
      key: 'note_id',
      label: 'Note',
      render: (value: string | null) =>
        value && value.length > 0 ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-green-600 break-all font-mono">{value}</span>
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  if (value) copy(value, 'Note ID');
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </div>
        ) : (
          <span className="text-sm text-gray-400">No note</span>
        ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <Badge color={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatDateTime(value)}</span>
      ),
    },
  ];

  const templatesColumns = [
    {
      key: 'title',
      label: 'Template Name',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => (
        <span className="text-sm font-medium text-gray-900">
          {row.revision?.title || 'Unnamed Template'}
        </span>
      ),
    },
    {
      key: 'id',
      label: 'Template ID',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => {
        const templateId = row.template?.id || '';
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-mono">
              {templateId
                ? templateId.length > 20
                  ? templateId.substring(0, 20) + '...'
                  : templateId
                : 'N/A'}
            </span>
            {templateId && (
              <Tooltip title="Copy ID">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    copy(templateId, 'Template ID');
                  }}
                >
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      key: 'owner_scope',
      label: 'Scope',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => {
        const scope = row.template?.owner_scope || 'user';
        const scopeColors: Record<string, 'primary' | 'secondary' | 'success' | 'default'> = {
          public: 'primary',
          org: 'success',
          user: 'secondary',
        };
        return (
          <Chip
            label={scope}
            size="small"
            color={scopeColors[scope] || 'default'}
            variant="outlined"
          />
        );
      },
    },
    {
      key: 'processing_version',
      label: 'Processing',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => {
        const version = row.template?.note_processing_version ?? 2;
        return (
          <Chip
            label={`V${version}`}
            size="small"
            color={version === 2 ? 'info' : 'default'}
            variant={version === 2 ? 'filled' : 'outlined'}
          />
        );
      },
    },
    {
      key: 'version',
      label: 'Version',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => (
        <span className="text-sm text-gray-600">
          v{row.revision?.version || row.template?.current_version || 1}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => (
        <span className="text-sm text-gray-600">
          {row.template?.created_at ? formatDateTime(row.template.created_at) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'updated_at',
      label: 'Modified',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => (
        <span className="text-sm text-gray-600">
          {row.template?.updated_at ? formatDateTime(row.template.updated_at) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: unknown, row: EffectiveNoteTemplateSummary) => {
        const scope = row.template?.owner_scope || 'user';
        const isPublicOrOrg = scope === 'public' || scope === 'org';
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isPublicOrOrg && (
              <Tooltip title="Fork for User">
                <IconButton
                  size="small"
                  onClick={e => handleOpenForkDialog(row, e)}
                  color="primary"
                >
                  <CallSplit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {scope === 'user' && (
              <Tooltip title="Convert to Organization Template">
                <IconButton size="small" onClick={e => handleConvertClick(row, e)} color="warning">
                  <Business fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Copy Template">
              <IconButton size="small" onClick={e => handleCopyClick(row, e)} color="primary">
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
            {scope === 'org' ? (
              <>
                {provider?.organization_id && (
                  <Tooltip title="Manage org templates">
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        navigate(`/admin-v2/organization/${provider.organization_id}`);
                      }}
                      color="primary"
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Org templates can only be managed from the organization page">
                  <span>
                    <IconButton size="small" disabled>
                      <Delete fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : scope === 'public' ? (
              <Tooltip title="Remove from this user's list">
                <IconButton
                  size="small"
                  onClick={e => handleOpenRemoveDialog(row, 'remove', e)}
                  color="warning"
                >
                  <PersonRemove fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Permanently delete this template">
                <IconButton
                  size="small"
                  onClick={e => handleOpenRemoveDialog(row, 'delete', e)}
                  color="error"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const streamingThreadsColumns = [
    {
      key: 'thread_id',
      label: 'Thread ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 font-mono">
            {value?.substring(0, 12)}...
          </span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  copy(value, 'Thread ID');
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'initial_message',
      label: 'Initial Message',
      render: (value: string) => (
        <Typography
          variant="body2"
          sx={{
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>
      ),
    },
    {
      key: 'event_count',
      label: 'Events',
      render: (value: number) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'first_message_at',
      label: 'Started',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatRelativeTime(value)}</span>
      ),
    },
    {
      key: 'last_message_at',
      label: 'Last Activity',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatRelativeTime(value)}</span>
      ),
    },
  ];

  return (
    <div className="flex h-full overflow-hidden bg-gray-50">
      {/* Workspace takeover: this sidebar renders in the global admin
          sidebar's exact slot with its exact shell (AdminLayout hides the
          global one here). The hierarchy is global → org → provider, so its
          header exits up one level: to this provider's org, or to the admin
          when the provider has no organization. */}
      <WorkspaceSidebar
        groups={buildProviderNavGroups(
          !!provider.organization_id,
          notesData?.total,
          currentDosespotStatus,
        )}
        activeKey={activeSection}
        onSelect={handleSectionSelect}
        exitLabel={provider.organization_id ? 'Organization' : 'Admin v2'}
        onExit={() =>
          navigate(
            provider.organization_id
              ? `/admin-v2/organization/${provider.organization_id}`
              : '/admin-v2',
          )
        }
      />

      <div className="min-w-0 flex-1 overflow-y-auto">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Passport: everything that identifies the provider (name, status,
            roles, copyable ID/contact chips, the org hop) plus provider-level
            actions, in one calm sticky band. */}
          <ProviderPassport
            provider={provider}
            onChangeEmail={() => setChangeEmailDialogOpen(true)}
            onEditPhone={() => setEditPhoneDialogOpen(true)}
            onDeletePhone={() => setDeletePhoneDialogOpen(true)}
            onOpenOrganization={organizationId =>
              navigate(`/admin-v2/organization/${organizationId}`)
            }
          />

          <div className="py-6">
            {activeSection === 'overview' && (
              <>
                {provider.organization_id ? (
                  <ProviderEntitlementsSection
                    userId={provider.provider_id}
                    organizationId={provider.organization_id}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No organization associated with this user.
                  </Typography>
                )}
              </>
            )}

            {activeSection === 'bundles' && provider.organization_id && (
              <ProviderBundleAssignmentsSection
                userId={provider.provider_id}
                organizationId={provider.organization_id}
              />
            )}

            {activeSection === 'feature-flags' && (
              <ProviderFeatureFlags
                providerId={providerId || ''}
                currentFeatureFlags={provider?.feature_flags}
              />
            )}

            {activeSection === 'feature-gates' && (
              <ProviderStatsigGates
                providerId={providerId || ''}
                organizationId={provider?.organization_id || ''}
              />
            )}

            {activeSection === 'voice-enrollment' && (
              <VoiceEnrollmentSection userId={provider.provider_id} />
            )}

            {activeSection === 'google-calendar' && (
              <Box>
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-1">Sync Status</div>
                  {googleSyncLoading ? (
                    <div className="text-sm text-gray-500">Loading…</div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex items-center justify-between rounded border px-3 py-2">
                        <span className="text-sm text-gray-700">Synced</span>
                        {googleSync?.is_configured ? (
                          <Badge color="success">true</Badge>
                        ) : (
                          <Badge color="error">false</Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between rounded border px-3 py-2">
                        <span className="text-sm text-gray-700">Integrated</span>
                        {googleSync?.is_integrated ? (
                          <Badge color="success">true</Badge>
                        ) : (
                          <Badge color="error">false</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  {googleSync?.is_integrated ? (
                    <Button
                      variant="contained"
                      disabled={initiating}
                      onClick={() => {
                        if (!providerId) return;
                        void initiateSync({ providerId });
                      }}
                    >
                      create and sync calendar
                    </Button>
                  ) : (
                    <Tooltip title="User must integrate Google Calendar in the app to enable syncing. This happens when they click the Sync Calendar button in the Schedule tab.">
                      <span>
                        <Button variant="contained" disabled>
                          initiate calendar sync
                        </Button>
                      </span>
                    </Tooltip>
                  )}

                  <Button
                    variant="outlined"
                    color="error"
                    disabled={deleting}
                    onClick={() => {
                      if (!providerId) return;
                      void deleteCalendar({ providerId });
                    }}
                  >
                    delete calendar
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    disabled={deleting}
                    onClick={() => {
                      if (!providerId) return;
                      void deleteCalendar({ providerId, resetIntegrationFlag: true });
                    }}
                  >
                    de-integrate from google
                  </Button>
                </div>
              </Box>
            )}

            {activeSection === 'dosespot' &&
              (() => {
                const orgConnected = !!providerOrg?.dosespot_connected;
                const orgClinicId = providerOrg?.dosespot_clinic_id ?? null;
                const clinicianId = provider?.dosespot_clinician_id ?? null;

                return (
                  <Box>
                    {/* Layer 1: Org connection status (shared shell with org page) */}
                    <Box sx={{ mb: 3 }}>
                      {providerOrgLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">
                            Checking organization DoseSpot connection…
                          </Typography>
                        </Box>
                      ) : orgConnected ? (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                        >
                          <Chip
                            icon={<CheckCircle fontSize="small" />}
                            label="Org Connected"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                          {orgClinicId && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                              <span>
                                Clinic ID: <code>{orgClinicId}</code>
                              </span>
                              <Tooltip title="Copy DoseSpot clinic ID">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    copy(orgClinicId, 'Clinic ID');
                                  }}
                                >
                                  <ContentCopy fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Alert
                          severity="info"
                          action={
                            provider?.organization_id ? (
                              <Button
                                component={Link}
                                to={`/admin-v2/organization/${provider.organization_id}`}
                                size="small"
                                color="inherit"
                              >
                                Open Org
                              </Button>
                            ) : undefined
                          }
                        >
                          This provider&apos;s organization is not connected to DoseSpot.
                          Prescribing is unavailable until the org is connected.
                        </Alert>
                      )}
                    </Box>

                    {/* Layer 2: per-provider state (only meaningful when the org is connected) */}
                    {orgConnected && (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Provider onboarding:
                          </Typography>
                          {clinicianId ? (
                            <Badge
                              color={
                                currentDosespotStatus === 'completed'
                                  ? 'success'
                                  : currentDosespotStatus === 'tfa_needed'
                                    ? 'warning'
                                    : currentDosespotStatus === 'idp_needed'
                                      ? 'info'
                                      : 'default'
                              }
                            >
                              {currentDosespotStatus || 'completed'}
                            </Badge>
                          ) : (
                            <Badge color="default">Not Onboarded</Badge>
                          )}
                          {provider?.organization_id && provider?.provider_id && (
                            <PrescribingProxyToggle
                              organizationId={provider.organization_id}
                              userId={provider.provider_id}
                            />
                          )}
                        </Box>
                        {clinicianId && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}
                          >
                            <span>
                              Clinician ID: <code>{clinicianId}</code>
                            </span>
                            <Tooltip title="Copy DoseSpot clinician ID">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  copy(clinicianId, 'Clinician ID');
                                }}
                              >
                                <ContentCopy fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Typography>
                        )}

                        <div className="flex items-end gap-3">
                          <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="dosespot-status-label">Onboarding Status</InputLabel>
                            <Select
                              labelId="dosespot-status-label"
                              value={dosespotStatus}
                              label="Onboarding Status"
                              onChange={e =>
                                setDosespotStatus(e.target.value as DosespotOnboardingStatus)
                              }
                            >
                              <MenuItem value="idp_needed">idp_needed</MenuItem>
                              <MenuItem value="tfa_needed">tfa_needed</MenuItem>
                              <MenuItem value="completed">completed</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={
                              isUpdatingDosespot ||
                              !dosespotStatus ||
                              dosespotStatus === currentDosespotStatus
                            }
                            onClick={async () => {
                              if (!providerId || !dosespotStatus) return;
                              try {
                                await updateDosespotOnboardingStatus({
                                  userId: providerId,
                                  onboardingStatus: dosespotStatus,
                                }).unwrap();
                                enqueueSnackbar('DoseSpot onboarding status updated', {
                                  variant: 'success',
                                });
                              } catch (error: any) {
                                enqueueSnackbar(
                                  error?.data?.message ||
                                    error?.data?.detail ||
                                    'Failed to update DoseSpot onboarding status',
                                  { variant: 'error' },
                                );
                              }
                            }}
                          >
                            {isUpdatingDosespot ? <CircularProgress size={20} /> : 'Save'}
                          </Button>
                        </div>
                      </>
                    )}
                  </Box>
                );
              })()}

            {activeSection === 'note-processing-sessions' && (
              <DataTable
                title="Note Processing Sessions"
                columns={noteProcessingSessionsColumns as any}
                data={noteProcessingSessionsData?.items || []}
                total={noteProcessingSessionsData?.total}
                loading={_noteProcessingSessionsLoading}
                getRowUrl={getNoteProcessingSessionsRowUrl}
                onViewAll={() => {
                  navigate(getNoteProcessingSessionsViewAllUrl());
                }}
                emptyMessage="No note processing sessions to display"
              />
            )}

            {activeSection === 'streaming-threads' && (
              <DataTable<StreamingThreadListItem>
                title="Streaming Threads (Note Chat)"
                columns={streamingThreadsColumns as any}
                data={streamingThreadsData?.threads || []}
                loading={_streamingThreadsLoading}
                getRowUrl={getStreamingThreadsRowUrl}
                onViewAll={() => {
                  navigate(getStreamingThreadsViewAllUrl());
                }}
                emptyMessage="No streaming threads found"
              />
            )}

            {activeSection === 'templates' && (
              <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1, p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="h6">Templates</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total: {(templatesData?.templates ?? []).length}
                  </Typography>
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Same templates and order as the provider sees.
                  </Typography>
                </Alert>

                <Tabs
                  value={templatesScope}
                  onChange={(_, newValue) => {
                    setTemplatesScope(newValue);
                    setTemplatesPage(1);
                  }}
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                >
                  <Tab label="All" value="all" />
                  <Tab label="Public" value="public" />
                  <Tab label="Organization" value="org" />
                  <Tab label="User" value="user" />
                </Tabs>

                <FilterableDataTable
                  title="Provider Templates"
                  columns={templatesColumns as any}
                  data={paginatedTemplates}
                  total={totalTemplates}
                  loading={_templatesLoading}
                  page={templatesPage}
                  perPage={templatesPerPage}
                  onPageChange={setTemplatesPage}
                  onPerPageChange={n => {
                    setTemplatesPerPage(n);
                    setTemplatesPage(1);
                  }}
                  getRowUrl={getTemplatesRowUrl}
                  emptyMessage="No templates found for this provider"
                />
              </Box>
            )}

            {activeSection === 'encounter-types' && (
              <ProviderEncounterTypesSection
                providerId={providerId}
                templates={templatesData?.templates}
              />
            )}
          </div>
        </main>

        {/* Copy Template Dialog */}
        <CopyTemplateDialog
          open={copyDialogOpen}
          onClose={() => {
            setCopyDialogOpen(false);
            setTemplateForAction(null);
          }}
          template={templateForAction}
        />

        {/* Convert to Organization Template Dialog */}
        <ConvertToOrgDialog
          open={convertDialogOpen}
          onClose={() => {
            setConvertDialogOpen(false);
            setTemplateForAction(null);
          }}
          template={templateForAction}
          defaultOrganizationId={provider?.organization_id}
          defaultOrganizationName={provider?.organization_name}
        />

        {/* Fork Template Dialog */}
        <Dialog open={forkDialogOpen} onClose={handleCloseForkDialog} fullWidth maxWidth={'sm'}>
          <DialogTitle>Fork Template for User</DialogTitle>
          <DialogContent>
            {selectedTemplate && (
              <>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Create a personalized copy of this template for{' '}
                  <strong>{provider?.name || providerId}</strong>.
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Template:</strong>{' '}
                    {selectedTemplate.revision?.title || 'Unnamed Template'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Type:</strong> {selectedTemplate.template?.owner_scope || 'user'}
                  </Typography>
                </Alert>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={bypassForkRestriction}
                      onChange={e => setBypassForkRestriction(e.target.checked)}
                    />
                  }
                  label="Force fork even if template doesn't allow forks"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', ml: 4, mt: -1 }}
                >
                  Enable this if the template has fork restrictions but you need to create a copy
                  anyway.
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForkDialog} disabled={isForking}>
              Cancel
            </Button>
            <Button
              onClick={handleForkTemplate}
              variant="contained"
              disabled={isForking || !selectedTemplate}
              startIcon={isForking ? <CircularProgress size={16} /> : <CallSplit />}
            >
              {isForking ? 'Forking...' : 'Fork Template'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Remove/Delete Template Dialog */}
        <Dialog open={removeDialogOpen} onClose={handleCloseRemoveDialog} maxWidth={'xs'}>
          <DialogTitle>
            {removeAction === 'delete'
              ? 'Permanently Delete Template'
              : 'Remove Template from User'}
          </DialogTitle>
          <DialogContent>
            {removeAction === 'delete' ? (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  This action cannot be undone.
                </Alert>
                <Typography>
                  This will permanently delete{' '}
                  <strong>{templateToRemove?.revision?.title || 'this template'}</strong>. It will
                  no longer be available to <strong>{provider?.name || providerId}</strong> or
                  anyone else.
                </Typography>
              </>
            ) : (
              <Typography>
                This will remove{' '}
                <strong>{templateToRemove?.revision?.title || 'this template'}</strong> from{' '}
                <strong>{provider?.name || providerId}</strong>&apos;s template list. The template
                will still be available to other users.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRemoveDialog} disabled={isRemoving || isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmTemplateAction}
              variant="contained"
              color="error"
              disabled={isRemoving || isDeleting}
              startIcon={isRemoving || isDeleting ? <CircularProgress size={16} /> : undefined}
            >
              {isRemoving || isDeleting
                ? removeAction === 'delete'
                  ? 'Deleting...'
                  : 'Removing...'
                : removeAction === 'delete'
                  ? 'Delete Forever'
                  : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Phone Dialog */}
        <EditProviderPhoneDialog
          open={editPhoneDialogOpen}
          onClose={() => setEditPhoneDialogOpen(false)}
          providerId={provider.provider_id}
          providerName={provider.name}
          currentPhone={provider.user_info?.mobile_phone_num}
        />

        {/* Change Login Email Dialog */}
        <ChangeProviderEmailDialog
          open={changeEmailDialogOpen}
          onClose={() => setChangeEmailDialogOpen(false)}
          providerId={provider.provider_id}
          providerName={provider.name}
          currentEmail={provider.user_info?.email}
        />

        {/* Delete Phone Confirmation Dialog */}
        <Dialog
          open={deletePhoneDialogOpen}
          onClose={() => setDeletePhoneDialogOpen(false)}
          maxWidth={'sm'}
        >
          <DialogTitle>Remove Phone Number</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2">
                Are you sure you want to remove the phone number for{' '}
                <strong>{provider.name}</strong>?
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Current number: <strong>{provider.user_info?.mobile_phone_num}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                They will no longer receive SMS notifications at this number.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeletePhoneDialogOpen(false)} disabled={isDeletingPhone}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={isDeletingPhone}
              onClick={async () => {
                try {
                  await deletePhone({
                    providerId: provider.provider_id,
                    updateProviderProfileRequest: {
                      mobile_phone_num: null,
                    },
                  }).unwrap();
                  enqueueSnackbar('Phone number removed successfully', { variant: 'success' });
                  setDeletePhoneDialogOpen(false);
                } catch (error: any) {
                  enqueueSnackbar(
                    error?.data?.message || error?.data?.detail || 'Failed to remove phone number',
                    { variant: 'error' },
                  );
                }
              }}
            >
              {isDeletingPhone ? <CircularProgress size={20} /> : 'Remove Phone'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}
